/**
 * Payment Link Expiration Utilities
 *
 * Utilities untuk menentukan waktu expiration yang tepat untuk countdown timer
 * dengan mempertimbangkan expired_at dari PaymentLink (plink) dan PaymentLinkHistory (plh)
 */

/**
 * Get the earliest expiration time between PaymentLink and PaymentLinkHistory
 *
 * Logic:
 * 1. Jika kedua expired_at ada, gunakan yang paling cepat
 * 2. Jika hanya satu yang ada, gunakan yang ada
 * 3. Jika keduanya null/undefined, return null (akan default ke 1 jam di CountDownTimer)
 *
 * @param {string|null} paymentLinkExpiredAt - expired_at dari PaymentLink model (ISO 8601 format)
 * @param {string|null} paymentLinkHistoryExpiredAt - expired_at dari PaymentLinkHistory model (ISO 8601 format)
 * @returns {string|null} - ISO 8601 timestamp yang paling cepat expired, atau null
 */
export const getEarliestExpiration = (paymentLinkExpiredAt, paymentLinkHistoryExpiredAt) => {
  // Convert to Date objects for comparison
  const plinkDate = paymentLinkExpiredAt ? new Date(paymentLinkExpiredAt) : null;
  const plhDate = paymentLinkHistoryExpiredAt ? new Date(paymentLinkHistoryExpiredAt) : null;

  // Jika keduanya null, return null
  if (!plinkDate && !plhDate) {
    return null;
  }

  // Jika salah satu null, return yang ada
  if (!plinkDate) {
    return paymentLinkHistoryExpiredAt;
  }
  if (!plhDate) {
    return paymentLinkExpiredAt;
  }

  // Jika keduanya ada, return yang paling cepat
  // Return the earlier date (smaller timestamp)
  return plinkDate < plhDate ? paymentLinkExpiredAt : paymentLinkHistoryExpiredAt;
};

/**
 * Calculate remaining time in milliseconds until expiration
 *
 * @param {string|null} expiredAt - ISO 8601 timestamp
 * @returns {number} - Remaining milliseconds (0 if expired or null)
 */
export const getRemainingTime = (expiredAt) => {
  if (!expiredAt) {
    return 0;
  }

  const now = new Date();
  const expirationDate = new Date(expiredAt);
  const remaining = expirationDate.getTime() - now.getTime();

  return remaining > 0 ? remaining : 0;
};

/**
 * Check if a timestamp is expired
 *
 * @param {string|null} expiredAt - ISO 8601 timestamp
 * @returns {boolean} - true if expired or null
 */
export const isExpired = (expiredAt) => {
  if (!expiredAt) {
    return false; // null means no expiration set
  }

  const now = new Date();
  const expirationDate = new Date(expiredAt);

  return now > expirationDate;
};

/**
 * Format remaining time for display
 *
 * @param {string|null} expiredAt - ISO 8601 timestamp
 * @returns {Object} - { days, hours, minutes, seconds, expired }
 */
export const formatRemainingTime = (expiredAt) => {
  const remaining = getRemainingTime(expiredAt);

  if (remaining <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  const seconds = Math.floor((remaining / 1000) % 60);
  const minutes = Math.floor((remaining / (1000 * 60)) % 60);
  const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, expired: false };
};

/**
 * Get expiration info with detailed breakdown
 * Useful for debugging or detailed display
 *
 * @param {string|null} paymentLinkExpiredAt
 * @param {string|null} paymentLinkHistoryExpiredAt
 * @returns {Object}
 */
export const getExpirationInfo = (paymentLinkExpiredAt, paymentLinkHistoryExpiredAt) => {
  const earliestExpiration = getEarliestExpiration(paymentLinkExpiredAt, paymentLinkHistoryExpiredAt);
  const plinkExpired = isExpired(paymentLinkExpiredAt);
  const plhExpired = isExpired(paymentLinkHistoryExpiredAt);

  let source = 'default'; // 'plink', 'plh', or 'default'
  if (earliestExpiration) {
    source = earliestExpiration === paymentLinkExpiredAt ? 'plink' : 'plh';
  }

  return {
    earliestExpiration,
    source,
    paymentLinkExpiredAt,
    paymentLinkHistoryExpiredAt,
    paymentLinkExpired: plinkExpired,
    paymentLinkHistoryExpired: plhExpired,
    isExpired: earliestExpiration ? isExpired(earliestExpiration) : false,
    remainingTime: earliestExpiration ? getRemainingTime(earliestExpiration) : 0,
  };
};

/**
 * Calculate countdown expiry for method selection with default timeout
 *
 * Logic:
 * - Default countdown: 5 minutes from now
 * - If paymentLinkExpiredAt is sooner than 5 minutes, use that instead
 * - If paymentLinkExpiredAt is null or more than 5 minutes, use 5 minutes default
 *
 * @param {string|null} paymentLinkExpiredAt - expired_at dari PaymentLink (ISO 8601)
 * @param {number} defaultMinutes - Default countdown dalam menit (default: 5)
 * @returns {string} - ISO 8601 timestamp untuk countdown
 */
export const getMethodSelectionExpiry = (paymentLinkExpiredAt, defaultMinutes = 5) => {
  // Calculate default expiry (5 minutes from now)
  const defaultExpiry = new Date(Date.now() + defaultMinutes * 60 * 1000);

  // If no payment link expiration, use default
  if (!paymentLinkExpiredAt) {
    return defaultExpiry.toISOString();
  }

  // Parse payment link expiration
  const plExpiry = new Date(paymentLinkExpiredAt);

  // Use whichever is earlier
  return plExpiry < defaultExpiry ? paymentLinkExpiredAt : defaultExpiry.toISOString();
};

export default {
  getEarliestExpiration,
  getRemainingTime,
  isExpired,
  formatRemainingTime,
  getExpirationInfo,
  getMethodSelectionExpiry,
};
